import { Label, Panel, Snackbar, Toast } from "@mcleod/components";
import { HorizontalAlignment } from "@mcleod/core";

/**
 * Utility class for displaying user-facing success and error notifications
 * related to Triumph payment submissions and updates.
 */
export class TriumphNotifications {

    /**
     * Formats Triumph error messages according to the standardized format.
     * Backend already provides fully formatted error messages, so this primarily
     * handles null/empty cases with a fallback message.
     *
     * @param error Error message from backend (already formatted)
     * @param isUpdate Whether this is an update (true) or submission (false)
     * @returns Error message or fallback if empty
     */
    static formatError(error: string, isUpdate: boolean): string {
        if (!error || error.trim() === '') {
            return isUpdate
                ? "Triumph update failed. See log for details"
                : "Triumph submission failed. See log for details";
        }

        // Backend already provides formatted message (e.g., "Triumph Submission Error: ...")
        // Just return it as-is
        return error;
    }

    /**
     * Shows a warning snackbar with a formatted Triumph error message.
     *
     * @param error Raw error message from backend
     * @param isUpdate Whether this is an update (true) or submission (false)
     * @param targetPanel Optional target panel for the snackbar
     */
    static showErrorSnackbar(error: string, isUpdate: boolean, targetPanel?: any): void {
        const formattedError = this.formatError(error, isUpdate);
        Snackbar.showWarningSnackbar(formattedError, targetPanel ? { targetPanel } : undefined);
    }

    /**
     * Shows a success toast when a Triumph payment is successfully canceled.
     *
     * @param orderId Order ID
     * @param payeeName Payee name
     * @param payTotal Total payment amount (optional, only shown for Settlement cancellations)
     */
    static showCancellationSuccess(orderId: string, payeeName: string, payTotal?: string): void {
        const message = payTotal
            ? `The Triumph payment of ${payTotal} for Order ${orderId} - ${payeeName} was canceled successfully.`
            : `The Triumph payment for Order ${orderId} - ${payeeName} was canceled successfully.`;

        this.showSuccessToast(message);
    }

    /**
     * Shows a success toast when a Triumph payment update is successfully sent.
     *
     * @param orderId Order ID
     */
    static showUpdateSuccess(orderId: string): void {
        const message = `Triumph update sent successfully for Order ${orderId}`;
        this.showSuccessToast(message);
    }

    /**
     * Shows a success toast when a single settlement is successfully submitted to Triumph.
     *
     * @param orderId Order ID
     * @param payeeName Payee name
     */
    static showSubmissionSuccess(orderId: string, payeeName: string): void {
        const message = `Settlement for Order ${orderId} - ${payeeName} has been successfully sent to Triumph.`;
        this.showSuccessToast(message);
    }

    /**
     * Shows a success toast for bulk settlement submissions or updates.
     *
     * @param successfulCount Number of successful submissions/updates
     * @param totalAmount Total amount submitted/updated (formatted as currency string)
     * @param isUpdate Whether this was an update (true) or new submission (false)
     */
    static showBulkSubmissionSuccess(successfulCount: number, totalAmount: string, isUpdate: boolean): void {
        const actionVerb = isUpdate ? "updated" : "submitted";
        const destinationText = isUpdate ? "" : " to Triumph";
        const message = `${successfulCount} settlement(s) ${actionVerb}${destinationText} for a total of ${totalAmount}.`;

        this.showSuccessToast(message, 10000); // Longer duration for bulk operations
    }

    /**
     * Internal helper to create and show a success toast.
     *
     * @param message The success message to display
     * @param duration Duration in milliseconds (default 7000)
     */
    private static showSuccessToast(message: string, duration: number = 7000): void {
        const panelContainer = new Panel({ align: HorizontalAlignment.CENTER });

        const headerMessage = new Label({
            text: "Success!",
            fontSize: "large",
            rowBreak: true,
            align: HorizontalAlignment.CENTER
        });

        const successMessage = new Label({
            text: message,
            rowBreak: true,
            align: HorizontalAlignment.CENTER
        });

        panelContainer.add(headerMessage);
        panelContainer.add(successMessage);

        Toast.showToast(panelContainer, { backgroundColor: "success" }, {
            millisUntilDismissal: duration
        });
    }
}
